import { useState } from "react";
import { Search, Sparkles, Loader2, Users, Building2, ShoppingBag, MapPin, Phone, MessageCircle, CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP = "918142445677";

const SUGGESTIONS = [
  "I need a plumber in Madhapur",
  "Show me 2BHK flats for rent in Kokapet",
  "Maid available near Gachibowli",
  "Used washing machine for sale",
  "Electrician in HITEC City",
  "Villa for sale in Kondapur",
];

const AISearchBar = () => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleSearch = async (searchQuery) => {
    const q = searchQuery || query;
    if (!q.trim()) return;
    setLoading(true);
    setResults(null);
    try {
      const res = await axios.post(`${API}/ai/search`, { query: q });
      setResults(res.data);
    } catch {
      setResults({ type: "error", suggestion: "AI search is temporarily unavailable. Please browse sections below.", results: [], matched_count: 0 });
    }
    setLoading(false);
  };

  const handleSuggestion = (s) => {
    setQuery(s);
    handleSearch(s);
  };

  const typeIcon = { workers: Users, properties: Building2, marketplace: ShoppingBag, mixed: Search };
  const typeColor = { workers: "text-marigold", properties: "text-sage", marketplace: "text-terracotta", mixed: "text-indigo" };

  return (
    <div data-testid="ai-search">
      {/* Search Input */}
      <div className="relative">
        <div className="flex items-center bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="pl-4">
            <Sparkles size={18} className="text-marigold" />
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Ask Junction AI — &quot;I need a plumber in Kokapet&quot;"
            className="flex-1 px-3 py-4 text-sm text-gray-800 placeholder:text-gray-400 outline-none bg-transparent"
            data-testid="ai-search-input"
          />
          <Button onClick={() => handleSearch()} disabled={loading} className="m-1.5 bg-indigo hover:bg-indigo/90 text-white rounded-xl px-5 h-10" data-testid="ai-search-button">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <><Search size={14} className="mr-1.5" />Search</>}
          </Button>
        </div>
      </div>

      {/* Quick Suggestions */}
      {!results && (
        <div className="flex flex-wrap gap-2 mt-3">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => handleSuggestion(s)} className="text-[11px] px-3 py-1.5 rounded-full bg-white/80 text-gray-500 border border-gray-100 hover:border-indigo hover:text-indigo transition-all" data-testid={`ai-suggestion-${s.slice(0, 15).toLowerCase().replace(/\s+/g, '-')}`}>
              {s}
            </button>
          ))}
        </div>
      )}

      {/* AI Results */}
      {results && (
        <div className="mt-4 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid="ai-results">
          {/* Header */}
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-marigold" />
              <span className="text-xs font-medium text-gray-600">
                {results.matched_count > 0 ? `Found ${results.matched_count} results` : "AI Suggestion"}
              </span>
              {results.type !== "error" && (
                <Badge className={`rounded-full text-[10px] ${typeColor[results.type]} bg-gray-100`}>
                  {results.type}
                </Badge>
              )}
            </div>
            <button onClick={() => setResults(null)} className="text-xs text-gray-400 hover:text-gray-600">Clear</button>
          </div>

          {/* Suggestion */}
          {results.suggestion && (
            <div className="px-4 py-3 text-sm text-gray-600 bg-marigold/5 border-b border-gray-100">
              <Sparkles size={12} className="inline mr-1.5 text-marigold" />
              {results.suggestion}
            </div>
          )}

          {/* Results list */}
          {results.results && results.results.length > 0 && (
            <div className="divide-y divide-gray-50">
              {results.results.map((r, i) => (
                <div key={i} className="px-4 py-3 hover:bg-gray-50 transition-colors flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-indigo">{r.name || r.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {r.skill && <span>{r.skill} </span>}
                      {r.type && <span>{r.type} </span>}
                      {r.area && <span>in {r.area} </span>}
                      {r.location && <span>in {r.location} </span>}
                      {r.price && <span>- {r.price}</span>}
                    </p>
                  </div>
                  {r.skill && (
                    <a href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(`Hi, I need a ${r.skill} (${r.name}) in ${r.area} from Junction.`)}`} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" className="rounded-lg text-xs bg-green-600 hover:bg-green-700 text-white h-7 px-3">
                        <MessageCircle size={12} className="mr-1" />Contact
                      </Button>
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AISearchBar;
